<script setup lang="ts">
import { api } from '@/api';
import { type CreateOrUpdateProtocolDto } from '@/api/generator';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore';
import { useTranslation } from 'i18next-vue';
import { useRouter } from 'vue-router';
import ProtocolForm from './components/ProtocolForm.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { useProtocolsStore } from '@/stores/protocolsStore.ts';
import { backendValidator } from '@/validation/validators/backendValidator';
import { formBackendErrorHandler } from '@/errorHandling';

const { t } = useTranslation();
const notificationStore = useNotificationStore();
const router = useRouter();
const protocolStore = useProtocolsStore();

const initialValues: Partial<CreateOrUpdateProtocolDto> = {
  name: t('Untitled'),
  isAvailable: true,
};

const { form } = useVeeForm<CreateOrUpdateProtocolDto>({
  schemaName: 'CreateOrUpdateProtocolDto',
  options: { initialValues },
  mode: 'add',
  customValidateFunctions: [
    backendValidator((createOrUpdateProtocolDto, errorHandlers) =>
      api.protocolsApi.validateProtocol(
        {
          createOrUpdateProtocolDto,
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, values } = form;

const submit = handleSubmit(async (createOrUpdateProtocolDto) => {
  const { success, result: protocol } = await api.protocolsApi.createProtocol(
    {
      createOrUpdateProtocolDto,
    },
    [formBackendErrorHandler(form)],
  );

  if (success) {
    notificationStore.success(t('EntityAdded', { name: protocol.name, iuid: protocol.iuid }));
    await protocolStore.update();
    await router.push({
      name: routenames.protocols.overview,
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage>
      <template #title>
        <BasePageTitle :subtitle="$t('Protocol_one')" :title="values.name" />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.protocols.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <ProtocolForm />
    </BasePage>
  </form>
</template>
